import { SwapTokenPair } from '@plug-core/interfaces/swapInterface'
import { IconButton, ListItem, ListItemText, Stack, Typography } from '@mui/material'
import { useSwapContext } from '@plug-core/providers/SwapProvider'
import React, { FC, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { AnimationPlayer } from '@components/AnimationPlayer'
import successAnimation from '@assets/lottie/success.json'
import { useTranslation } from 'react-i18next'
import ExchangeReview from './ExchangeReview'
import { HiCog } from 'react-icons/hi'
import SwapConfig from '@components/SwapConfig'
import useSwapReview from '@plug-core/hooks/useSwapReview'

interface Props {
  performSwap: (pair: SwapTokenPair, quote: number) => Promise<number>
  currentStep?: string
  onError: (error: string) => void
}

const SwapReview: FC<Props> = ({ performSwap, currentStep, onError }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { fromToken, toToken, toAmount, amount } = useSwapContext()

  const { userApprovedToAmount, swapLoading, swapSuccess, swapDetails, doSwap } = useSwapReview({
    performSwap,
    onError,
    close: () => navigate(-1),
    chain: toToken?.chain,
  })

  const { swapConfigVisible, setSwapConfigVisible } = useSwapContext()

  const buttonTitle = useMemo(() => {
    if (!swapLoading) return t('swap.title')

    return t(`swap.steps.${currentStep}`)
  }, [swapLoading, currentStep, t])

  if (swapSuccess) {
    return (
      <Stack>
        <Typography variant="h6" textAlign="center" mt={2}>
          {t('swap.success')}
        </Typography>
        <AnimationPlayer animationData={successAnimation} />
      </Stack>
    )
  }

  return (
    <ExchangeReview
      fromTokenImage={fromToken!.logo}
      toTokenImage={toToken!.logo}
      fromTokenSymbol={fromToken!.symbol}
      toTokenSymbol={toToken!.symbol}
      loading={swapLoading}
      toAmount={swapLoading ? userApprovedToAmount : toAmount}
      amount={amount}
      handleSubmit={doSwap}
      buttonTitle={buttonTitle}
      kind="swap"
    >
      <Stack>
        {swapDetails.map(({ title, value, adjustable }) => (
          <ListItem key={title}>
            <ListItemText primary={title} />
            <Stack direction="row" alignItems="center">
              <ListItemText secondary={value} sx={{ textAlign: 'right' }} />
              {adjustable && (
                <IconButton color="primary" onClick={() => setSwapConfigVisible(true)}>
                  <HiCog size={18} />
                </IconButton>
              )}
            </Stack>
          </ListItem>
        ))}
      </Stack>
      <SwapConfig visible={swapConfigVisible} close={() => setSwapConfigVisible(false)} />
    </ExchangeReview>
  )
}

export default SwapReview
